import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeftRight, AlertTriangle } from "lucide-react";
import { comparisonsApi } from "../api/endpoints";
import { SkeletonCard } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { FeasibilityBadge } from "../components/chips/FeasibilityBadge";
import { shortHash } from "../lib/format";
import { cn } from "../lib/utils";

/**
 * Phase 6e — Run comparison viewer.
 *
 * Deterministic diff only. NO AI in this page. Executive language like
 * "resilience improved by X%" is intentionally avoided — see labels below.
 */

const TRANSITION_LABEL = {
  IMPROVED: { label: "Improved", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  WORSENED: { label: "Worsened", tone: "bg-red-50 text-red-800 border-red-200" },
  BECAME_DETERMINABLE: {
    label: "Became determinable",
    tone: "bg-sky-50 text-sky-800 border-sky-200",
  },
  BECAME_INDETERMINATE: {
    label: "Became indeterminate",
    tone: "bg-amber-50 text-amber-800 border-amber-200",
  },
  UNCHANGED: { label: "Unchanged", tone: "bg-slate-50 text-slate-700 border-slate-200" },
  CHANGED: { label: "Changed", tone: "bg-slate-50 text-slate-700 border-slate-200" },
  PROCESS_ADDED: {
    label: "Process added",
    tone: "bg-indigo-50 text-indigo-800 border-indigo-200",
  },
  PROCESS_REMOVED: {
    label: "Process removed",
    tone: "bg-slate-50 text-slate-700 border-slate-200",
  },
};

const FINDING_CATEGORY_LABEL = {
  ADDED: "Newly triggered",
  RESOLVED: "No longer triggered",
  CHANGED: "Same subject, changed facts",
  PERSISTING_UNCHANGED: "Persisting unchanged",
};

const DRIVER_LABEL = {
  SOURCE_DATA_CHANGE: "Source data change",
  RULE_DRIFT: "Rule drift",
  MAPPING_DRIFT: "Mapping drift",
  MULTIPLE_FACTORS: "Multiple factors",
  UNKNOWN_CAUSE: "Cause not attributable",
};

function TransitionChip({ transition }) {
  const meta = TRANSITION_LABEL[transition] || TRANSITION_LABEL.CHANGED;
  return (
    <span
      data-testid={`transition-chip-${transition}`}
      className={cn(
        "inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-medium",
        meta.tone,
      )}
    >
      {meta.label}
    </span>
  );
}

function DriverChip({ driver }) {
  return (
    <span
      data-testid={`driver-chip-${driver}`}
      className={cn(
        "inline-flex items-center rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 uppercase tracking-wider",
      )}
      title="Deterministic attribution — never AI-generated"
    >
      {DRIVER_LABEL[driver] || driver}
    </span>
  );
}

function DriftBanner({ tone, icon: Icon, title, children, testId }) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        "rounded border px-4 py-3 flex gap-3",
        tone === "warn"
          ? "border-amber-300 bg-amber-50 text-amber-900"
          : "border-sky-300 bg-sky-50 text-sky-900",
      )}
    >
      <Icon size={18} className="mt-0.5 shrink-0" />
      <div className="text-sm">
        <div className="font-semibold">{title}</div>
        <div className="mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, testId, tone = "neutral" }) {
  const toneClass = {
    neutral: "border-slate-200 bg-white text-slate-900",
    positive: "border-emerald-200 bg-emerald-50 text-emerald-900",
    negative: "border-red-200 bg-red-50 text-red-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
  }[tone];
  return (
    <div
      data-testid={testId}
      className={cn(
        "rounded border p-4 flex flex-col gap-1",
        toneClass,
      )}
    >
      <span className="text-[11px] uppercase tracking-wider opacity-75">
        {label}
      </span>
      <span className="text-2xl font-semibold">{value}</span>
    </div>
  );
}

function RunColumn({ label, run, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded border border-slate-200 bg-white p-4"
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="mt-1 font-medium text-slate-900 truncate" title={run.name || run.id}>
        {run.name || run.id}
      </div>
      <div className="mt-2 text-[11px] text-slate-500 font-mono">
        id {shortHash(run.id)}
      </div>
      <div className="text-[11px] text-slate-500 font-mono">
        rule lib v{run.rule_library_version || "?"} · {shortHash(run.rule_library_hash || "")}
      </div>
      {run.mapping_content_hash ? (
        <div className="text-[11px] text-slate-500 font-mono">
          mapping {shortHash(run.mapping_content_hash)}
        </div>
      ) : (
        <div className="text-[11px] text-slate-400 font-mono">
          JSON ingest — no mapping
        </div>
      )}
    </div>
  );
}

export default function RunComparisonPage() {
  const { workspaceId, runA, runB } = useParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [findingCategory, setFindingCategory] = useState("");

  const load = () => {
    setError(null);
    setResult(null);
    comparisonsApi.compare(workspaceId, runA, runB).then(setResult).catch(setError);
  };
  useEffect(load, [workspaceId, runA, runB]);

  const filteredFindings = useMemo(() => {
    if (!result) return [];
    let items = result.finding_changes || [];
    if (findingCategory) {
      items = items.filter((f) => f.category === findingCategory);
    }
    return items;
  }, [result, findingCategory]);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!result) return <SkeletonCard />;

  const s = result.summary;
  return (
    <div className="space-y-6" data-testid="run-comparison-page">
      <header className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          <Link
            to={`/w/${workspaceId}/analyses`}
            className="hover:text-slate-900"
          >
            Analyses
          </Link>{" "}
          / Compare
        </div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="run-comparison-title">
              Run comparison
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Deterministic diff. No AI is invoked on this page. Findings that
              no longer trigger in the newer analysis are reported as such —
              not framed as real-world remediation.
            </p>
          </div>
          <span
            data-testid="run-comparison-content-hash"
            className="font-mono text-[11px] text-slate-500"
            title={result.content_hash}
          >
            content_hash {shortHash(result.content_hash)}
          </span>
        </div>
        <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr] items-center">
          <RunColumn label="From" run={result.from_run} testId="run-comparison-from" />
          <ArrowRight className="mx-auto text-slate-400" size={20} />
          <RunColumn label="To" run={result.to_run} testId="run-comparison-to" />
        </div>
      </header>

      {result.methodology_drift?.rule_library_changed && (
        <DriftBanner
          tone="warn"
          icon={AlertTriangle}
          title="Rule library changed between these runs"
          testId="rule-drift-banner"
        >
          Some differences may reflect rule edits, not actual change in the
          BCM environment. Affected rules:{" "}
          <span className="font-mono">
            {[
              ...(result.methodology_drift.rule_diff.rules_added || []),
              ...(result.methodology_drift.rule_diff.rules_removed || []),
              ...(result.methodology_drift.rule_diff.config_changes || []).map((c) => c.rule_id),
              ...(result.methodology_drift.rule_diff.severity_changes || []).map((c) => c.rule_id),
              ...(result.methodology_drift.rule_diff.version_changes || []).map((c) => c.rule_id),
            ]
              .filter((v, i, a) => a.indexOf(v) === i)
              .sort()
              .join(", ") || "—"}
          </span>
        </DriftBanner>
      )}

      {result.mapping_drift?.mapping_changed && (
        <DriftBanner
          tone="warn"
          icon={ArrowLeftRight}
          title="Import mapping changed between these runs"
          testId="mapping-drift-banner"
        >
          Some differences may reflect changes in how source data was mapped
          or normalized rather than changes in the underlying BCM
          environment.
        </DriftBanner>
      )}

      <section aria-labelledby="summary-heading" className="space-y-3">
        <h2 id="summary-heading" className="text-sm font-semibold uppercase tracking-wider text-slate-600">
          Summary
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard testId="summary-improved" label="Processes improved" value={s.processes_improved} tone="positive" />
          <SummaryCard testId="summary-worsened" label="Processes worsened" value={s.processes_worsened} tone="negative" />
          <SummaryCard testId="summary-became-determinable" label="Became determinable" value={s.processes_became_determinable} tone="neutral" />
          <SummaryCard testId="summary-became-indeterminate" label="Became indeterminate" value={s.processes_became_indeterminate} tone="warn" />
          <SummaryCard testId="summary-added-processes" label="Processes added" value={s.processes_added} tone="neutral" />
          <SummaryCard testId="summary-removed-processes" label="Processes removed" value={s.processes_removed} tone="neutral" />
          <SummaryCard testId="summary-findings-added" label="Findings newly triggered" value={s.findings_added} tone="negative" />
          <SummaryCard testId="summary-findings-resolved" label="Findings no longer triggered" value={s.findings_resolved} tone="positive" />
        </div>
      </section>

      <ProcessChangesTable processes={result.process_changes || []} workspaceId={workspaceId} runB={runB} />

      <FindingChangesList
        findings={filteredFindings}
        totalCount={(result.finding_changes || []).length}
        category={findingCategory}
        setCategory={setFindingCategory}
      />

      <RuleDiffPanel diff={result.methodology_drift?.rule_diff} />
    </div>
  );
}

function ProcessChangesTable({ processes, workspaceId, runB }) {
  const [category, setCategory] = useState("");
  const shown = useMemo(() => {
    if (!category) return processes;
    return processes.filter((p) => p.transition === category);
  }, [processes, category]);
  if (processes.length === 0) return null;

  return (
    <section aria-labelledby="process-changes-heading" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 id="process-changes-heading" className="text-sm font-semibold uppercase tracking-wider text-slate-600">
          Process transitions
        </h2>
        <label className="inline-flex items-center gap-1 text-xs text-slate-600">
          Filter
          <select
            data-testid="process-transition-filter"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="">All ({processes.length})</option>
            {Object.entries(TRANSITION_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="rounded border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="process-transitions-table">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Process</th>
                <th className="px-4 py-2.5">Transition</th>
                <th className="px-4 py-2.5">From</th>
                <th className="px-4 py-2.5">To</th>
                <th className="px-4 py-2.5">Change driver</th>
                <th className="px-4 py-2.5">Dimension changes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((p) => (
                <tr key={p.process_id} data-testid={`process-change-row-${p.process_id}`}>
                  <td className="px-4 py-3">
                    {p.transition === "PROCESS_REMOVED" ? (
                      <span className="text-slate-700">{p.process_name_from || p.process_id}</span>
                    ) : (
                      <Link
                        to={`/w/${workspaceId}/analyses/${runB}/processes/${p.process_id}`}
                        className="text-slate-900 font-medium hover:underline"
                      >
                        {p.process_name_to || p.process_name_from || p.process_id}
                      </Link>
                    )}
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">{p.process_id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <TransitionChip transition={p.transition} />
                  </td>
                  <td className="px-4 py-3">
                    {p.from_status ? <FeasibilityBadge status={p.from_status} size="md" /> : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {p.to_status ? <FeasibilityBadge status={p.to_status} size="md" /> : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <DriverChip driver={p.change_driver} />
                  </td>
                  <td className="px-4 py-3 text-[11px] text-slate-600">
                    {p.dimension_changes?.length ? (
                      p.dimension_changes
                        .map((d) => `${d.dimension}: ${d.from_status || "—"} → ${d.to_status || "—"}`)
                        .join(" · ")
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function FindingChangesList({ findings, totalCount, category, setCategory }) {
  return (
    <section aria-labelledby="finding-changes-heading" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 id="finding-changes-heading" className="text-sm font-semibold uppercase tracking-wider text-slate-600">
          Finding changes
        </h2>
        <label className="inline-flex items-center gap-1 text-xs text-slate-600">
          Category
          <select
            data-testid="finding-category-filter"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="">All ({totalCount})</option>
            {Object.entries(FINDING_CATEGORY_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
      </div>
      {findings.length === 0 ? (
        <EmptyState title="No finding changes match those filters" />
      ) : (
        <ul className="space-y-2" data-testid="finding-changes-list">
          {findings.map((fc) => (
            <li
              key={fc.finding_instance_key}
              data-testid={`finding-change-row-${fc.finding_instance_key}`}
              className="rounded border border-slate-200 bg-white p-4"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    "inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium",
                    fc.category === "ADDED"
                      ? "bg-red-100 text-red-800"
                      : fc.category === "RESOLVED"
                      ? "bg-emerald-100 text-emerald-800"
                      : fc.category === "CHANGED"
                      ? "bg-amber-100 text-amber-900"
                      : "bg-slate-100 text-slate-700",
                  )}
                  data-testid={`finding-change-category-${fc.finding_instance_key}`}
                >
                  {FINDING_CATEGORY_LABEL[fc.category] || fc.category}
                </span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
                  {fc.rule_id}
                </span>
                <DriverChip driver={fc.change_driver} />
                {fc.severity_delta && (
                  <span className="text-[11px] text-slate-600">
                    severity {fc.severity_delta.from} → {fc.severity_delta.to}
                  </span>
                )}
                {fc.process_id && (
                  <span className="font-mono text-[11px] text-slate-500">
                    {fc.process_id}
                  </span>
                )}
                {fc.process_ids && fc.process_ids.length > 0 && (
                  <span className="font-mono text-[11px] text-slate-500">
                    {fc.process_ids.join(", ")}
                  </span>
                )}
              </div>
              {(fc.to_snapshot?.calculation || fc.from_snapshot?.calculation) && (
                <p className="mt-2 text-sm text-slate-800">
                  {fc.to_snapshot?.calculation || fc.from_snapshot?.calculation}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RuleDiffPanel({ diff }) {
  if (!diff) return null;
  const any =
    (diff.rules_added && diff.rules_added.length) ||
    (diff.rules_removed && diff.rules_removed.length) ||
    (diff.config_changes && diff.config_changes.length) ||
    (diff.severity_changes && diff.severity_changes.length) ||
    (diff.version_changes && diff.version_changes.length);
  if (!any) return null;
  return (
    <section aria-labelledby="rule-diff-heading" className="space-y-3">
      <h2 id="rule-diff-heading" className="text-sm font-semibold uppercase tracking-wider text-slate-600">
        Rule library diff
      </h2>
      <div className="rounded border border-slate-200 bg-white p-4 space-y-3 text-sm" data-testid="rule-diff-panel">
        {diff.rules_added?.length > 0 && (
          <div data-testid="rule-diff-added">
            <span className="font-semibold text-slate-800">Added: </span>
            <span className="font-mono">{diff.rules_added.join(", ")}</span>
          </div>
        )}
        {diff.rules_removed?.length > 0 && (
          <div data-testid="rule-diff-removed">
            <span className="font-semibold text-slate-800">Removed: </span>
            <span className="font-mono">{diff.rules_removed.join(", ")}</span>
          </div>
        )}
        {diff.config_changes?.length > 0 && (
          <div data-testid="rule-diff-config">
            <div className="font-semibold text-slate-800 mb-1">Config changes</div>
            <ul className="space-y-1 pl-4 list-disc">
              {diff.config_changes.map((c) => (
                <li key={c.rule_id} className="font-mono text-[12px]">
                  {c.rule_id}: {(c.changed_keys || []).join(", ")}
                </li>
              ))}
            </ul>
          </div>
        )}
        {diff.severity_changes?.length > 0 && (
          <div data-testid="rule-diff-severity">
            <div className="font-semibold text-slate-800 mb-1">Severity changes</div>
            <ul className="space-y-1 pl-4 list-disc">
              {diff.severity_changes.map((c) => (
                <li key={c.rule_id} className="font-mono text-[12px]">
                  {c.rule_id}: {c.from_severity} → {c.to_severity}
                </li>
              ))}
            </ul>
          </div>
        )}
        {diff.version_changes?.length > 0 && (
          <div data-testid="rule-diff-version">
            <div className="font-semibold text-slate-800 mb-1">Version changes</div>
            <ul className="space-y-1 pl-4 list-disc">
              {diff.version_changes.map((c) => (
                <li key={c.rule_id} className="font-mono text-[12px]">
                  {c.rule_id}: v{c.from_version} → v{c.to_version}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
